import { useState } from "react";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  ArrowLeft,
  Ban,
  ChevronRight,
  Inbox,
  Package,
  Truck,
} from "lucide-react";
import { OrderChatPanel } from "@/features/order-messages/OrderChatPanel";
import { cn } from "@/lib/utils";
import { useDateFnsLocale } from "@/lib/format-dates";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";
import {
  formatOrderNumber,
  type ClientOrderDetailAddress,
  type ClientOrderStatusRow,
} from "@threadheads/shared";
import { ClientOrderStatusChip } from "./ClientOrderStatusChip";
import { ClientOrderTypeBadge } from "./ClientOrderTypeBadge";
import { ClientOrderArtworkThumb } from "./ClientOrderArtworkThumb";
import { CopyableTracking } from "./CopyableTracking";

interface ClientOrderDetailPaneProps {
  /** The selected list row — supplies header context (number, status, date)
   *  instantly while the detail query loads. Null when nothing is selected. */
  order: ClientOrderStatusRow | null;
  /** Single-pane mode only: return to the list pane. */
  onBack: () => void;
  /** Whether the Back affordance is needed — true in single-pane mode. The
   *  page owns this (it measures available width); a viewport media query here
   *  would disagree with the pane switch whenever the sidebar is collapsed. */
  showBack?: boolean;
  /** Unread order-message count for this order (drives the chat's "New"
   *  divider). */
  unreadCount?: number;
}

const EMPTY_VALUE = "-";

function formatAddress(address: ClientOrderDetailAddress | null): string[] {
  if (!address) return [];
  const lines: string[] = [];
  if (address.address1) lines.push(address.address1);
  if (address.address2) lines.push(address.address2);
  const cityLine = [
    address.city,
    [address.province, address.zip].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");
  if (cityLine) lines.push(cityLine);
  if (address.country) lines.push(address.country);
  return lines;
}

/**
 * BLU-665 — the right pane of the two-pane client-orders inbox (Layout C).
 * Re-houses the BLU-660 detail sheet's content: sticky header (order #,
 * colour status chip, customer, date, tracking), a summary strip (ship-to,
 * method, tracking), line items, and a placeholder conversation region (the
 * Phase 3 chat lands here). Read-only — no money, no batch/station links,
 * no print-file access (BLU-654).
 */
export function ClientOrderDetailPane({
  order,
  onBack,
  showBack = false,
  unreadCount = 0,
}: ClientOrderDetailPaneProps) {
  const { t } = useTranslation("clientOrders");
  const shopifyOrderId = order?.shopifyOrderId ?? null;
  const [detailsOpen, setDetailsOpen] = useState(true);
  const dateLocale = useDateFnsLocale();

  const detailQuery = trpc.clientOrders.detail.useQuery(
    { shopifyOrderId: shopifyOrderId as number },
    {
      enabled: shopifyOrderId != null,
      staleTime: 30_000,
    },
  );

  if (!order) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-10 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Inbox className="h-7 w-7" aria-hidden="true" />
        </div>
        <h3 className="text-[15px] font-semibold">
          {t("detail.emptySelection.title")}
        </h3>
        <p className="max-w-[290px] text-sm text-muted-foreground">
          {t("detail.emptySelection.description")}
        </p>
      </div>
    );
  }

  const detail = detailQuery.data;
  const addressLines = formatAddress(detail?.shippingAddress ?? null);
  const orderDate = order.createdAt
    ? format(new Date(order.createdAt), "d MMM yyyy", { locale: dateLocale })
    : null;

  // Total units, not distinct line count — a client reading "Items (3)"
  // expects three garments, not three SKU rows.
  const totalQuantity =
    detail?.lineItems.reduce((sum, item) => sum + item.quantity, 0) ?? 0;

  const hasShippingInfo =
    !!detail?.recipientName ||
    addressLines.length > 0 ||
    !!detail?.shippingMethod ||
    !!detail?.trackingNumber;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Header */}
      <header className="flex shrink-0 items-start gap-3 border-b px-4 py-3 sm:px-5">
        {showBack ? (
          <Button
            variant="outline"
            size="sm"
            onClick={onBack}
            className="mt-0.5 h-8 shrink-0 gap-1.5 px-2.5"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            {t("detail.back")}
          </Button>
        ) : null}
        <div className="min-w-0 flex-1">
          {/* tabIndex -1: InboxSplitView focuses it when the single-pane detail opens. */}
          <h2
            tabIndex={-1}
            className="flex flex-wrap items-center gap-2 text-base font-semibold tracking-tight focus:outline-none"
          >
            <Package
              className="h-4 w-4 shrink-0 text-muted-foreground"
              aria-hidden="true"
            />
            {order.orderNumber
              ? t("detail.title", { number: formatOrderNumber(order.orderNumber) })
              : t("detail.titleFallback")}
            <ClientOrderStatusChip status={order.clientStatus} />
            {order.orderType ? (
              <ClientOrderTypeBadge type={order.orderType} />
            ) : null}
          </h2>
          <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
            {order.customerName ? <span>{order.customerName}</span> : null}
            {order.customerName && orderDate ? (
              <span aria-hidden="true">·</span>
            ) : null}
            {orderDate ? <span>{orderDate}</span> : null}
            {(order.tags ?? []).map((tag) => (
              <span
                key={tag}
                className="inline-flex max-w-[140px] items-center truncate rounded-[5px] border bg-muted px-1.5 py-px text-[11px] font-medium"
              >
                {tag}
              </span>
            ))}
          </p>
        </div>
        {order.trackingNumber ? (
          <div className="flex shrink-0 flex-col items-end">
            <CopyableTracking
              trackingNumber={order.trackingNumber}
              carrier={order.trackingCarrier}
              className="-mr-1.5"
            />
            {order.trackingCarrier ? (
              <span className="pr-1.5 text-[11px] text-muted-foreground">
                {order.trackingCarrier}
              </span>
            ) : null}
          </div>
        ) : null}
        <Button
          variant="outline"
          size="sm"
          className="mt-0.5 h-8 shrink-0 gap-1 px-2.5"
          aria-expanded={detailsOpen}
          onClick={() => setDetailsOpen((open) => !open)}
        >
          <ChevronRight
            className={cn(
              "h-4 w-4 transition-transform duration-200 motion-reduce:transition-none",
              detailsOpen && "rotate-90",
            )}
            aria-hidden="true"
          />
          {t("detail.detailsToggle")}
        </Button>
      </header>

      {/* A terminal warning must stay visible even when the optional order
          details are collapsed. It is driven by the list-row status, so it
          can render immediately without waiting for the detail query. */}
      {order.clientStatus === "cancelled" ? (
        <div
          data-testid="client-order-cancelled-notice"
          role="status"
          aria-atomic="true"
          className="flex shrink-0 items-center gap-2 border-b bg-red-50 px-4 py-4 text-sm text-red-800 sm:px-5"
        >
          <Ban className="h-4 w-4 shrink-0" aria-hidden="true" />
          {t("detail.cancelledNotice")}
        </div>
      ) : null}

      {/* Collapsible order details (summary + line items). Fixed: it never
          scrolls with the conversation — the chat below owns the remaining
          height and is the pane's only scroller (Layout C: conversation-
          forward). The cap is a safety valve for pathological line-item counts
          only: it reserves 240px for the conversation so the composer can never
          be pushed out of view, and it is measured against THIS pane (definite
          height), not the viewport. Typical orders fit under it and never
          scroll; the Details toggle collapses the block entirely. */}
      <div
        className={cn(
          "shrink-0 border-b",
          detailsOpen ? "max-h-[calc(100%-240px)] overflow-y-auto" : "hidden",
        )}
      >
        {detailQuery.isLoading ? (
          <DetailPaneSkeleton />
        ) : detailQuery.isError ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <AlertTriangle
              className="h-10 w-10 text-destructive"
              aria-hidden="true"
            />
            <p className="mt-3 text-base font-medium">
              {t("detail.error.title")}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("detail.error.description")}
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-4"
              onClick={() => void detailQuery.refetch()}
            >
              {t("detail.error.action")}
            </Button>
          </div>
        ) : detail ? (
          <div className="motion-safe:animate-in motion-safe:fade-in-50 motion-safe:duration-200">
            {/* Summary strip */}
            {hasShippingInfo ? (
              <section
                aria-label={t("detail.summaryAria")}
                className="grid grid-cols-1 gap-x-6 gap-y-3 border-b bg-muted/30 px-4 py-4 sm:grid-cols-2 sm:px-5"
              >
                <div>
                  <p className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {t("detail.shipTo")}
                  </p>
                  <div className="mt-1 text-sm">
                    {detail.recipientName ? (
                      <p className="font-medium">{detail.recipientName}</p>
                    ) : null}
                    {addressLines.length > 0 ? (
                      <address className="not-italic text-muted-foreground">
                        {addressLines.map((line, index) => (
                          <span key={index} className="block">
                            {line}
                          </span>
                        ))}
                      </address>
                    ) : !detail.recipientName ? (
                      <p className="text-muted-foreground">{EMPTY_VALUE}</p>
                    ) : null}
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {t("detail.shippingMethod")}
                    </p>
                    <p className="mt-1 flex items-center gap-1.5 text-sm">
                      <Truck
                        className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                        aria-hidden="true"
                      />
                      {detail.shippingMethod ?? EMPTY_VALUE}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {t("detail.tracking")}
                    </p>
                    {detail.trackingNumber ? (
                      <div className="mt-0.5 flex flex-col items-start">
                        <CopyableTracking
                          trackingNumber={detail.trackingNumber}
                          carrier={detail.trackingCarrier}
                          className="-ml-1.5"
                        />
                        {detail.trackingCarrier ? (
                          <span className="pl-1.5 text-[11px] text-muted-foreground">
                            {detail.trackingCarrier}
                          </span>
                        ) : null}
                      </div>
                    ) : (
                      <p className="mt-1 text-sm text-muted-foreground">
                        {EMPTY_VALUE}
                      </p>
                    )}
                  </div>
                </div>
              </section>
            ) : (
              <p className="border-b px-4 py-4 text-sm text-muted-foreground sm:px-5">
                {t("detail.shippingUnavailable")}
              </p>
            )}

            {/* Line items */}
            <section className="px-4 py-4 sm:px-5">
              <h3 className="text-sm font-semibold">
                {t("detail.items")}{" "}
                <span className="font-normal text-muted-foreground tabular-nums">
                  ({totalQuantity})
                </span>
              </h3>

              {detail.lineItems.length === 0 ? (
                <p className="mt-3 rounded-lg border border-dashed py-8 text-center text-sm text-muted-foreground">
                  {t("detail.noItems")}
                </p>
              ) : (
                <ul className="mt-3 space-y-2">
                  {detail.lineItems.map((item, index) => (
                    <li
                      key={`${item.shopifySku ?? "item"}-${index}`}
                      className="flex items-center gap-3 rounded-lg border p-3"
                    >
                      <ClientOrderArtworkThumb
                        src={item.customPreviewUrl ?? item.imageUrl}
                        alt={item.productName}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium">
                          {item.productName}
                        </p>
                        {item.variantTitle ? (
                          <p className="truncate text-sm text-muted-foreground">
                            {item.variantTitle}
                          </p>
                        ) : null}
                        {item.shopifySku ? (
                          <p className="truncate font-mono text-xs text-muted-foreground">
                            {item.shopifySku}
                          </p>
                        ) : null}
                        {item.printArea ? (
                          <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                            {t(`printArea.${item.printArea}`)}
                          </p>
                        ) : null}
                      </div>
                      <div className="shrink-0 text-right">
                        <span className="text-xs text-muted-foreground">
                          {t("detail.qty")}
                        </span>
                        <p className="font-medium tabular-nums">
                          {item.quantity}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

          </div>
        ) : (
          <p className="py-12 text-center text-sm text-muted-foreground">
            {EMPTY_VALUE}
          </p>
        )}
      </div>

      {/* Conversation — full remaining height, composer pinned at the bottom.
          Keyed per order so composer draft / unread snapshot reset on
          selection change. */}
      {shopifyOrderId != null ? (
        <OrderChatPanel
          key={shopifyOrderId}
          mode="client"
          shopifyOrderId={shopifyOrderId}
          initialUnread={unreadCount}
          className="flex-1"
        />
      ) : null}
    </div>
  );
}

function DetailPaneSkeleton() {
  return (
    <div className="space-y-6 px-4 py-4 sm:px-5">
      <div className="space-y-3 rounded-lg border p-4">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-4 w-56" />
        <Skeleton className="h-4 w-32" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-4 w-16" />
        {Array.from({ length: 3 }).map((_, index) => (
          <div
            key={index}
            className="flex items-center gap-3 rounded-lg border p-3"
          >
            <Skeleton className="h-14 w-14 rounded-md" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
            <Skeleton className="h-8 w-8" />
          </div>
        ))}
      </div>
    </div>
  );
}
